import { CHAT_REFUSALS, MAX_CHAT_CHARS, MUTE_ALL, isChatChannel, isChatRefusal, type ChatRefusal, type MuteScope } from "odyssey-shared";
import { EN, type EnKey } from "odyssey-lang";
import { say } from "../../words.ts";
import type { PageRefusal } from "./command.ts";
import type { ChatCommand, CommandGroup } from "./commands.ts";
import { PING_COUNT } from "./ping.ts";

/**
 * The chat panel's wording that takes a decision: which of chat's words a wait, a scope, a command or a refusal
 * reads as. The words themselves are the language files' (`chat.*` in `odyssey-lang`, ADR-119); a panel that needs
 * one sentence as it stands asks `say` for it.
 */

/**
 * Seconds to show for a wait, rounded up, and at least one.
 *
 * @param waitMs The wait in milliseconds.
 * @returns Whole seconds.
 */
export function waitSeconds(waitMs: number): number {
  return Math.max(1, Math.ceil(waitMs / 1000));
}

/**
 * How long a mute lasts or has left, in words: whole minutes under an hour, whole hours under two days, whole days
 * past, each rounded up.
 *
 * @param waitMs The time in milliseconds.
 * @returns The time, "12 min", "5 h" or "3 days".
 */
export function muteTime(waitMs: number): string {
  const minutes = Math.max(1, Math.ceil(waitMs / 60_000));
  if (minutes < 60) {
    return say("chat.muteTime.minutes", { minutes: String(minutes) });
  }
  const hours = Math.ceil(minutes / 60);
  return hours < 48 ? say("chat.muteTime.hours", { hours: String(hours) }) : say("chat.muteTime.days", { days: String(Math.ceil(hours / 24)) });
}

/**
 * What a mute's scope covers, in words: one channel's name, or the three for `all`.
 *
 * @param scope The scope, as the server sent it.
 * @returns The words, "Trade" or "Global, Trade and Help".
 */
export function scopeWords(scope: MuteScope | string): string {
  if (scope === MUTE_ALL) {
    return say("chat.allChannels", { global: { k: "chat.scopes.global" }, trade: { k: "chat.scopes.trade" }, help: { k: "chat.scopes.help" } });
  }
  return isChatChannel(scope) ? say(`chat.scopes.${scope}`) : scope;
}

/**
 * The scope picker's name: where the draft goes, and that the button changes it.
 *
 * @param scope The scope's name, or null for a command, which goes to no scope.
 * @returns The name.
 */
export function pickerName(scope: string | null): string {
  return scope === null ? say("chat.picker.nowhere") : say("chat.picker.scope", { scope });
}

/**
 * The line that lists the families a player has blocked.
 *
 * @param families The families' names.
 * @returns The line.
 */
export function blockListLine(families: readonly string[]): string {
  return say("chat.blockList", { families: families.join(", ") });
}

/**
 * The line a wait before the next message is told in.
 *
 * @param seconds Whole seconds left.
 * @returns The line, "You can send again in 6 s."
 */
export function throttledLine(seconds: number): string {
  return say("chat.throttled", { seconds: String(seconds) });
}

/**
 * `/ping`'s line while its pings go out: the steps still to come, counted down.
 *
 * @param sent How many pings have gone out.
 * @returns The line, "Pinging server: 5, 4."
 */
export function pingingLine(sent: number): string {
  return say("chat.pinging", { steps: Array.from({ length: sent }, (_, step) => PING_COUNT - step).join(", ") });
}

/**
 * The commands that have a description, by id.
 */
const DESCRIBED = EN.words.chat.describe;

/**
 * The commands whose options have descriptions, by id.
 */
const OPTIONS: Readonly<Record<string, Readonly<Record<string, string>>>> = EN.words.chat.option;

/**
 * The name of an option's place among its command's options, as the language files key it.
 */
const OPTION_PLACES = ["first", "second", "third"] as const;

/**
 * A key of an option's description.
 */
type OptionKey = Extract<EnKey, `chat.option.${string}`>;

/**
 * Whether a command has a description.
 *
 * @param id The command's id.
 * @returns True for a described command.
 */
function isDescribed(id: string): id is keyof typeof DESCRIBED {
  return Object.hasOwn(DESCRIBED, id);
}

/**
 * The heading a group of the `/` list stands under.
 *
 * @param group The group.
 * @returns The heading, a label.
 */
export function groupHeading(group: CommandGroup): string {
  return say(`chat.groups.${group}`);
}

/**
 * An open command's description in the list.
 *
 * @param command The command.
 * @returns The description, a label; a reserved command, never listed, has an empty one.
 */
export function commandDescription(command: ChatCommand): string {
  return isDescribed(command.id) ? say(`chat.describe.${command.id}`) : "";
}

/**
 * An option's description in the hint.
 *
 * @param command The command.
 * @param index The option's place among its options.
 * @returns The description, a label, or an empty one for an option without.
 */
export function optionDescription(command: ChatCommand, index: number): string {
  const place = OPTION_PLACES[index];
  const described = Object.hasOwn(OPTIONS, command.id) ? OPTIONS[command.id] : undefined;
  if (place === undefined || described === undefined || !Object.hasOwn(described, place)) {
    return "";
  }
  return sayOption(command.id, place);
}

/**
 * Words one option's description, whose key is made from a command's id and the option's place.
 *
 * @param id The command's id, which has options with descriptions.
 * @param place The option's place, which the command describes.
 * @returns The description.
 */
function sayOption(id: string, place: (typeof OPTION_PLACES)[number]): string {
  return say(`chat.option.${id}.${place}` as OptionKey);
}

/**
 * The answer to a reserved command: its channel is not open yet, or, for a command that is no channel, the
 * command is not.
 *
 * @param command The reserved command.
 * @returns The sentence.
 * @example
 * closedSentence(commandNamed("g")!);
 * // "Global chat is not open yet."
 */
export function closedSentence(command: ChatCommand): string {
  return command.channel === null ? say("chat.closed.command", { word: command.words[0] ?? "" }) : say("chat.closed.channel", { channel: say(`chat.scopes.${command.channel}`) });
}

/**
 * The sentence for a reason the server refuses with. A wait is filled in where a reason carries one.
 *
 * @param reason The reason.
 * @param waitMs The wait the refusal carries, in milliseconds.
 * @returns The sentence.
 */
function refusalWords(reason: ChatRefusal, waitMs: number): string {
  switch (reason) {
    case "rate":
      return throttledLine(waitSeconds(waitMs));
    case "changes":
      return say("chat.refused.changes", { seconds: String(waitSeconds(waitMs)) });
    case "long":
      return say("chat.refused.long", { max: String(MAX_CHAT_CHARS) });
    case "muted":
      return say("chat.refused.muted", { time: muteTime(waitMs) });
    case "pace":
      return say("chat.refused.pace", { seconds: String(waitSeconds(waitMs)) });
    default:
      return say(`chat.refused.${reason}`);
  }
}

/**
 * The sentence for a refusal the server sent.
 *
 * @param reason The reason; one this build does not know, from a later server, gets a sentence of its own.
 * @param waitMs The wait the refusal carried, for a limit.
 * @returns The sentence.
 */
export function refusalSentence(reason: string, waitMs = 0): string {
  return isChatRefusal(reason) ? refusalWords(reason, waitMs) : say("chat.unknownRefusal");
}

/**
 * The sentence for an input the page refused without sending: the text rule's reasons in the server's words,
 * and the page's own for a slash naming no command, a whisper naming no full name, a block naming no family, a
 * reply with no one to reach, a join or a leave naming no channel, and a mute out of its syntax.
 *
 * @param reason The reason.
 * @returns The sentence.
 */
export function pageRefusalSentence(reason: PageRefusal): string {
  switch (reason) {
    case "command":
      return say("chat.command");
    case "target":
      return say("chat.target");
    case "family":
      return say("chat.family");
    case "nobody":
      return say("chat.nobody");
    case "channel":
      return say("chat.channelWord");
    case "mute":
      return say("chat.muteUsage");
    case "name":
      return say("party.command.name");
    default:
      return refusalSentence(reason);
  }
}

/**
 * Every reason the server names, for a gallery or a test that shows each sentence.
 */
export const SERVER_REFUSALS: readonly ChatRefusal[] = CHAT_REFUSALS;
